/* =====================================================================
 * UserActivityModal — 사용자별 요청 활동 내역 (owner 전용)
 *
 * 우측 슬라이드-인 드로어. user_usage_ledger 를 message_id(요청)로 묶어
 * 요청별 카드로 보여준다: 시각·기능·상태(완료/실패)·토큰. 질의/응답 본문은
 * 행별 "본문 보기"로 게이트 조회(서버 감사 로그 기록). 각 행은 독립 토글.
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { X, Loader2, ChevronDown, ChevronUp, Clock, RefreshCw } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import {
  getUserActivity,
  getUserActivityMessages,
  type UserActivityItem,
  type GatedMessageItem,
} from '@auw/api-client';
import { SelectDropdown, type SelectOption } from '../common';

const PAGE = 100;

// 활동 내역 필터 옵션 — i18n appManage.features.* / activity.status_* 와 정합.
const FEATURE_KEYS = [
  'agent', 'live-agent', 'doc-agent', 'cad-agent', 'cad-material',
  'estimate-agent', 'document', 'message', 'unknown',
] as const;
const STATUS_KEYS = ['completed', 'failed', 'cancelled', 'in_progress'] as const;

type Preset = 'all' | 'today' | 'week' | 'month' | 'custom';

const kstTodayISO = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
const addDaysISO = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

interface Props {
  userId: string;
  userName: string;
  onClose: () => void;
}

function formatTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export const UserActivityModal: React.FC<Props> = ({ userId, userName, onClose }) => {
  const { t } = useTranslation('admin');
  const addToast = useChatStore((s) => s.addToast);

  const [items, setItems] = useState<UserActivityItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // 필터: 기간(KST)·기능·상태.
  const today = kstTodayISO();
  const [preset, setPreset] = useState<Preset>('all');
  const [from, setFrom] = useState(() => addDaysISO(today, -6));
  const [to, setTo] = useState(today);
  const [feature, setFeature] = useState('');
  const [status, setStatus] = useState('');

  // 행별 독립 토글: 열린 요청의 message_id(요청당 유일).
  const [openMsg, setOpenMsg] = useState<string | null>(null);
  // 본문 캐시: message_id(요청) 단위.
  const [messages, setMessages] = useState<Record<string, GatedMessageItem[]>>({});
  const [msgLoading, setMsgLoading] = useState<string | null>(null);

  const load = useCallback(async (nextOffset: number) => {
    setLoading(true);
    try {
      const res = await getUserActivity(userId, {
        limit: PAGE,
        offset: nextOffset,
        dateFrom: preset === 'all' ? undefined : from,
        dateTo: preset === 'all' ? undefined : to,
        feature: feature || undefined,
        status: status || undefined,
      });
      setItems((prev) => (nextOffset === 0 ? res.items : [...prev, ...res.items]));
      setOffset(nextOffset);
      setHasMore(res.has_more);
    } catch (e) {
      addToast({ type: 'error', title: t('appManage.activity.loadFail'), description: e instanceof Error ? e.message : undefined });
    } finally {
      setLoading(false);
    }
  }, [userId, preset, from, to, feature, status, addToast, t]);

  // 필터 변경 시 첫 페이지부터 다시 로드.
  useEffect(() => { void load(0); }, [load]);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'today') { setFrom(today); setTo(today); }
    else if (p === 'week') { setFrom(addDaysISO(today, -6)); setTo(today); }
    else if (p === 'month') { setFrom(addDaysISO(today, -29)); setTo(today); }
  };

  const featureOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('appManage.activity.filterAllFeatures') },
      ...FEATURE_KEYS.map((f) => ({ value: f, label: t(`appManage.features.${f}`, f) })),
    ],
    [t],
  );
  const statusOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('appManage.activity.filterAllStatus') },
      ...STATUS_KEYS.map((s) => ({ value: s, label: t(`appManage.activity.status_${s}`, s) })),
    ],
    [t],
  );

  // 세션(session_id) 단위로 묶고, 그 안에서 message_id(요청)별로 구분한다.
  const groups = useMemo(() => {
    const m = new Map<string, UserActivityItem[]>();
    for (const it of items) {
      const sid = it.session_id || it.item_id || '-';
      const arr = m.get(sid);
      if (arr) arr.push(it);
      else m.set(sid, [it]);
    }
    return Array.from(m, ([sid, rows]) => ({ sid, rows }));
  }, [items]);

  // ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = useCallback(async (row: UserActivityItem) => {
    const msgKey = row.message_id ?? '';
    const itemId = row.item_id ?? '';
    if (!msgKey || !itemId) return;
    if (openMsg === msgKey) { setOpenMsg(null); return; }
    setOpenMsg(msgKey);
    if (!messages[msgKey]) {
      setMsgLoading(msgKey);
      try {
        const res = await getUserActivityMessages(userId, itemId, { messageId: msgKey, limit: 200 });
        setMessages((prev) => ({ ...prev, [msgKey]: res.messages }));
      } catch (e) {
        addToast({ type: 'error', title: t('appManage.activity.contentFail'), description: e instanceof Error ? e.message : undefined });
        setOpenMsg(null);
      } finally {
        setMsgLoading(null);
      }
    }
  }, [openMsg, messages, userId, addToast, t]);

  return (
    <Overlay onClick={onClose}>
      <Drawer onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <Head>
          <div>
            <HeadTitle>{t('appManage.activity.title', { name: userName })}</HeadTitle>
            <PrivacyNote>{t('appManage.activity.privacyNote')}</PrivacyNote>
          </div>
          <HeadActions>
            <RefreshBtn type="button" onClick={() => void load(0)} disabled={loading} aria-label={t('appManage.refresh')}>
              <RefreshCw size={15} className={loading ? 'spin' : undefined} />
              {t('appManage.refresh')}
            </RefreshBtn>
            <CloseBtn type="button" onClick={onClose} aria-label="close"><X size={20} /></CloseBtn>
          </HeadActions>
        </Head>

        <Filters>
          <Presets>
            {(['all', 'today', 'week', 'month'] as Preset[]).map((p) => (
              <PresetBtn key={p} type="button" $active={preset === p} onClick={() => applyPreset(p)}>
                {p === 'all' ? t('appManage.overview.preset_all') : t(`appManage.overview.preset_${p}`)}
              </PresetBtn>
            ))}
          </Presets>
          {preset !== 'all' && (
            <Range>
              <DateInput type="date" value={from} max={to} onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }} />
              <span>~</span>
              <DateInput type="date" value={to} max={today} min={from} onChange={(e) => { setPreset('custom'); setTo(e.target.value); }} />
            </Range>
          )}
          <FilterSelect
            options={featureOptions}
            value={feature}
            onChange={setFeature}
            size="sm"
            placeholder={t('appManage.activity.filterAllFeatures')}
          />
          <FilterSelect
            options={statusOptions}
            value={status}
            onChange={setStatus}
            size="sm"
            placeholder={t('appManage.activity.filterAllStatus')}
          />
        </Filters>

        <Body>
          {loading && items.length === 0 ? (
            <Center><Loader2 size={20} className="spin" /></Center>
          ) : items.length === 0 ? (
            <Center>{t('appManage.activity.empty')}</Center>
          ) : (
            <List>
              {groups.map((g) => {
                const totalTok = g.rows.reduce((a, r) => a + (r.tokens || 0), 0);
                return (
                  <SessionBlock key={g.sid}>
                    <SessionHeader>
                      <SessionLabel title={g.sid}>{t('appManage.activity.session')} · {g.sid.slice(0, 8)}</SessionLabel>
                      <SessionMeta>{t('appManage.activity.msgCount', { count: g.rows.length })} · {totalTok.toLocaleString()} {t('appManage.activity.tokens')}</SessionMeta>
                    </SessionHeader>
                    <SessionRows>
                      {g.rows.map((it, idx) => {
                        const msgKey = it.message_id ?? '';
                        const itemId = it.item_id ?? '';
                        const isOpen = !!msgKey && openMsg === msgKey;
                        return (
                          <Row key={`${msgKey || idx}`} $open={isOpen}>
                            <RowMain onClick={() => itemId && toggle(it)} $clickable={!!itemId}>
                              <RowTop>
                                <Time><Clock size={12} /> {formatTime(it.created_at)}</Time>
                                <StatusBadge $status={it.status}>
                                  {t(`appManage.activity.status_${it.status}`, it.status)}
                                </StatusBadge>
                              </RowTop>
                              <RowBottom>
                                <FeatureChip>{t(`appManage.features.${it.feature}`, it.feature)}</FeatureChip>
                                <Tokens>{it.tokens.toLocaleString()} {t('appManage.activity.tokens')}</Tokens>
                                {itemId && (
                                  <Expand>
                                    {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                    {isOpen ? t('appManage.activity.hideContent') : t('appManage.activity.viewContent')}
                                  </Expand>
                                )}
                              </RowBottom>
                            </RowMain>
                            {isOpen && (
                              <Content>
                                {msgLoading === msgKey ? (
                                  <Center><Loader2 size={16} className="spin" /></Center>
                                ) : (
                                  <Conversation>
                                    {(messages[msgKey] ?? []).map((m, i) => (
                                      <Msg key={m.message_id ?? i} $role={m.role}>
                                        <MsgRole>{t(`appManage.activity.role_${m.role}`, m.role)}</MsgRole>
                                        <MsgBubble $role={m.role}>{m.content}</MsgBubble>
                                      </Msg>
                                    ))}
                                    {(messages[msgKey] ?? []).length === 0 && <Center>{t('appManage.activity.empty')}</Center>}
                                  </Conversation>
                                )}
                              </Content>
                            )}
                          </Row>
                        );
                      })}
                    </SessionRows>
                  </SessionBlock>
                );
              })}
              {hasMore && (
                <MoreBtn type="button" disabled={loading} onClick={() => load(offset + PAGE)}>
                  {loading ? <Loader2 size={14} className="spin" /> : t('appManage.activity.loadMore')}
                </MoreBtn>
              )}
            </List>
          )}
        </Body>
      </Drawer>
    </Overlay>
  );
};

export default UserActivityModal;

/* ------------------------------------------------------------------ */
/* Styles — 우측 슬라이드 드로어                                         */
/* ------------------------------------------------------------------ */

const slideIn = keyframes`from { transform: translateX(100%); } to { transform: translateX(0); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: flex-end;
  z-index: 1000;
`;

const Drawer = styled.div`
  width: min(820px, 100%);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bgPrimary};
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
  animation: ${slideIn} 0.22s ease;
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const HeadTitle = styled.h2`
  margin: 0 0 4px;
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const PrivacyNote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const RefreshBtn = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const CloseBtn = styled.button`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  flex-shrink: 0;
  &:hover { color: ${({ theme }) => theme.colors.textPrimary}; }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

/* 기능/상태 드롭다운: 프리셋과 같은 행에서 남는 폭을 균등 분할(전체 폭 점유 방지). */
const FilterSelect = styled(SelectDropdown)`
  flex: 1;
  min-width: 120px;
`;

const Presets = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const PresetBtn = styled.button<{ $active?: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 5px 11px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active, theme }) => ($active ? theme.colors.textPrimary : theme.colors.textMuted)};
  background: ${({ $active, theme }) => ($active ? theme.colors.bgPrimary : 'transparent')};
`;

const Range = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
`;

const DateInput = styled.input`
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  color-scheme: ${({ theme }) => (theme.mode === 'dark' ? 'dark' : 'light')};
`;


const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 32px;
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const SessionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SessionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 2px;
`;

const SessionLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SessionMeta = styled.span`
  flex-shrink: 0;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const SessionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Row = styled.div<{ $open?: boolean }>`
  border: 1px solid ${({ theme, $open }) => ($open ? theme.colors.borderInput : theme.colors.borderLight)};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  overflow: hidden;
  transition: border-color 0.15s ease;
`;

const RowMain = styled.div<{ $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover { background: ${({ theme, $clickable }) => ($clickable ? theme.colors.bgSecondary : 'transparent')}; }
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Time = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const StatusBadge = styled.span<{ $status: string }>`
  flex-shrink: 0;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #fff;
  background: ${({ $status }) =>
    $status === 'completed'
      ? '#10b981'
      : $status === 'failed'
        ? '#ef4444'
        : $status === 'cancelled'
          ? '#f59e0b'
          : '#9ca3af'};
`;

const RowBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FeatureChip = styled.span`
  padding: 2px 9px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const Tokens = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const Expand = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Content = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const Conversation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
`;

const Msg = styled.div<{ $role: string }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
`;

const MsgRole = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const MsgBubble = styled.div<{ $role: string }>`
  max-width: 88%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 0.8125rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme, $role }) =>
    $role === 'user' ? theme.colors.bgUserMessage : theme.colors.bgPrimary};
`;

const MoreBtn = styled.button`
  appearance: none;
  margin-top: 6px;
  padding: 10px;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  cursor: pointer;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
  &:disabled { opacity: 0.6; cursor: default; }
`;
